import React, { memo, useRef, useCallback, useMemo } from 'react'

import { Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import FileFormatContent from 'Pods/SearchPage/Shared/FileFormatContent/FileFormatContent'
import {
  buildFileFormatOptions, POPULAR_FORMATS_COUNT,
} from 'Pods/SearchPage/Shared/FileFormatContent/fileFormatOptions'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { addFileType, clearFileTypes, removeFileType } from 'Pods/SearchPage/Store/actions/fileTypes'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import FiltersWrapper from 'Pods/Shared/FiltersWrapper/FiltersWrapper'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

const SEARCH_FOCUS_DELAY = 300

// Figma 2989:109512 names most formats by their name without the extensions ("Blender (.blend)" ->
// "Blender"); these few use an abbreviation instead. Keyed by the option name.
const SHORT_LABEL_OVERRIDES = {
  '3ds Max': 'Max',
  'Cinema 4D': 'C4D',
  Maya: 'MA, MB',
}

// Sizes the trigger for the formats Figma names; rarer, longer ones truncate.
const RESERVED_VALUE = 'Blender'

const toShortLabel = (label) => {
  const title = label.replace(/\s*\(.*\)\s*$/, '')

  return SHORT_LABEL_OVERRIDES[title] ?? title
}

function FileFormats() {
  const translate = useTranslate()

  const searchRef = useRef(null)
  const fileTypes = useSearchPageStore(state => state.fileTypes)
  const fileTypeOptions = useSearchPageStore(state => state.filtersOptions?.fileTypes?.filters)

  const selectedValues = useMemo(() => buildFileFormatOptions(fileTypeOptions ?? EMPTY_ARRAY)
    .filter(option => option.ids.some(id => fileTypes.includes(id)))
    .map(option => toShortLabel(option.label)), [fileTypeOptions, fileTypes])

  const handleReset = useCallback(() => {
    clearFileTypes()
  }, [])

  const handleFilterChange = useCallback((_, value, isOn) => {
    if (isOn) {
      addFileType(value)
    } else {
      removeFileType(value)
    }
  }, [])

  const onOpenChange = useCallback((isOpen) => {
    if (isOpen && searchRef.current) {
      setTimeout(() => {
        searchRef.current?.focus()
      }, SEARCH_FOCUS_DELAY)
    }
  }, [])

  const content = useMemo(() => (
    <div className="file-format-filter__wrapper">
      <FileFormatContent
        fileTypes={fileTypes}
        onFilterChange={handleFilterChange}
        searchRef={searchRef}
        limit={POPULAR_FORMATS_COUNT}
        hideShowMore={false}
        isListScrollable
      />
      <Button
        type="ghost"
        size="tiny"
        customClass="filter-wrapper__reset-button"
        onClick={handleReset}
        disabled={fileTypes.length === 0}
      >
        {translate('search_page.filters.more_filters.reset')}
      </Button>
    </div>
  ), [fileTypes, handleFilterChange, handleReset, translate])

  return (
    <FiltersWrapper
      trailIconSize="small"
      placeholder={translate('search_page.filters.file_format.title')}
      content={content}
      onOpenChange={onOpenChange}
      hasSelectedAnyFilters={!!fileTypes.length}
      selectedValues={selectedValues}
      reservedValue={RESERVED_VALUE}
      positionHorizontal="left"
      closingDropdownClassName="filter-wrapper"
    />
  )
}

export default memo(FileFormats)
